import { type AgentRunStatus, AGENT_RUN_STATUSES } from '@sbc/domain-models/gateway';
import { Link, useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAgentRunBindings, useAgentRuns } from '@/lib/api/agent-runs';
import { pick } from '@/lib/utils';

import { BindingStatusBadge } from './binding-row';
import { RunStatusBadge } from './run-status-badge';

const PAGE_SIZE = 20;
const ALL = 'all';

export interface RunsListSearch {
  status?: AgentRunStatus | typeof ALL;
  inFlight?: boolean;
  channelBindingId?: string;
  page?: number;
}

// Both keys are always present so a tab writes the whole filter — a spread of a partial
// preset would leave the tab it replaced half in the URL.
interface RunFilter {
  status: AgentRunStatus | typeof ALL | undefined;
  inFlight: true | undefined;
}

// An operator opens this screen to see what still needs them, so an empty URL is the
// in-flight tab rather than every run ever queued.
const DEFAULT_FILTER: RunFilter = { status: undefined, inFlight: true };

const RUN_TABS: { label: string; filter: RunFilter }[] = [
  { label: 'In-flight', filter: DEFAULT_FILTER },
  { label: 'Exhausted', filter: { status: 'exhausted', inFlight: undefined } },
  { label: 'All', filter: { status: ALL, inFlight: undefined } },
];

export function parseRunsListSearch(search: Record<string, unknown>): RunsListSearch {
  const page = typeof search.page === 'number' ? search.page : Number(search.page);
  return {
    status: search.status === ALL ? ALL : pick(AGENT_RUN_STATUSES, search.status),
    inFlight: search.inFlight === true || search.inFlight === 'true' ? true : undefined,
    channelBindingId:
      typeof search.channelBindingId === 'string' ? search.channelBindingId : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

function currentFilter(search: RunsListSearch): RunFilter {
  if (search.status === undefined && search.inFlight === undefined) {
    return DEFAULT_FILTER;
  }
  return { status: search.status, inFlight: search.inFlight ? true : undefined };
}

export function AgentRunsListPage({ search }: { search: RunsListSearch }) {
  const navigate = useNavigate();
  const page = search.page ?? 1;
  const filter = currentFilter(search);

  const update = (patch: Partial<RunsListSearch>) => {
    void navigate({
      to: '/gateway/runs',
      search: {
        status: search.status,
        inFlight: search.inFlight,
        channelBindingId: search.channelBindingId,
        page: undefined,
        ...patch,
      },
    });
  };

  const bindingsQuery = useAgentRunBindings();
  const runsQuery = useAgentRuns({
    page,
    pageSize: PAGE_SIZE,
    status: filter.status === ALL ? undefined : filter.status,
    inFlight: filter.inFlight,
    channelBindingId: search.channelBindingId,
  });

  const result = runsQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Agent runs</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Every run the gateway queued for an agent, and where it got to.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border p-1">
          {RUN_TABS.map((tab) => (
            <Button
              key={tab.label}
              size="sm"
              variant={
                tab.filter.status === filter.status && tab.filter.inFlight === filter.inFlight
                  ? 'secondary'
                  : 'ghost'
              }
              onClick={() => update(tab.filter)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
        <Select
          value={search.channelBindingId ?? ALL}
          onValueChange={(value) => update({ channelBindingId: value === ALL ? undefined : value })}
        >
          <SelectTrigger aria-label="Filter by binding" className="w-96">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All bindings</SelectItem>
            {bindingsQuery.data?.map((binding) => (
              <SelectItem key={binding.id} value={binding.id}>
                {binding.connectionHandle} · {binding.strategyKey} · {binding.agentName}
                {!binding.enabled && ' (disabled)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {runsQuery.isPending && <p className="text-sm">Loading…</p>}
      {runsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load agent runs: {runsQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Binding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead className="w-1/4">Error</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Link
                            to="/gateway/bindings/$bindingId/edit"
                            params={{ bindingId: run.channelBindingId }}
                            className="font-medium hover:underline"
                          >
                            {run.connectionHandle} · {run.strategyKey} · {run.agentName}
                          </Link>
                          {!run.bindingEnabled && <BindingStatusBadge enabled={false} />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <RunStatusBadge status={run.status} />
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        <div>{run.attempts}</div>
                        {run.resumes > 0 && (
                          <div className="text-xs text-[var(--color-muted-foreground)]">
                            {run.resumes} resumed
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className="max-w-xs truncate text-sm text-[var(--color-muted-foreground)]"
                        title={run.lastError ?? undefined}
                      >
                        {run.lastError ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {run.sessionId ? (
                          <Link
                            to="/sessions/$sessionId"
                            params={{ sessionId: run.sessionId }}
                            className="font-mono hover:underline"
                          >
                            {run.sessionId.slice(0, 8)}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(run.createdAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      Nothing here.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">{result.total} run(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => update({ page: page - 1 > 1 ? page - 1 : undefined })}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => update({ page: page + 1 })}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
